import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * "Continue with Google", when there is a Google to continue with.
 *
 * Renders **nothing at all** until the server says a provider is configured.
 * That is the whole design of this component: a button that leads to "this
 * provider is not set up" is worse than no button, because the person cannot
 * tell whether the fault is theirs, and the honest answer — an admin has not
 * finished a setup — is not one the sign-in page should be spelling out to a
 * stranger.
 *
 * Also silent on error. If the check itself fails, the password form below is
 * still perfectly usable, and a red banner about a feature nobody asked for is
 * noise on the one screen that has to work.
 */
/**
 * Are we inside another app’s browser?
 *
 * WhatsApp, Facebook, Instagram and Gmail open links in a WebView of their
 * own rather than in Chrome. Two things then go wrong with a provider
 * sign-in, and the person cannot see either of them:
 *
 *   · Google refuses to authenticate inside an embedded browser at all.
 *   · If the WebView hands off to Chrome instead, the state cookie stays
 *     behind in the WebView, and the callback lands on "that sign-in could
 *     not be completed in this browser".
 *
 * Either way the loop is unwinnable from inside, and the fix — open it in
 * Chrome — is not something anybody guesses. Better to say so before they
 * press the button than to explain it afterwards.
 *
 * Detection is by user agent, which is imperfect by nature. It is used only
 * to *add a warning*, never to hide the buttons: a false positive costs a
 * sentence somebody can ignore, and a false negative leaves things exactly
 * as they were.
 */
function inAppBrowser(): string | null {
  if (typeof navigator === 'undefined') return null;
  const agent = navigator.userAgent;
  if (/FBAN|FBAV|FB_IAB/i.test(agent)) return 'Facebook';
  if (/Instagram/i.test(agent)) return 'Instagram';
  if (/WhatsApp/i.test(agent)) return 'WhatsApp';
  if (/Line\//i.test(agent)) return 'LINE';
  /*
   * Android WebView. `wv` is the reliable marker; the Version/x.y form is
   * the older one. Deliberately last, because it is the loosest.
   */
  if (/\bwv\b/.test(agent) || /Version\/[\d.]+ Chrome/.test(agent)) return 'another app';
  return null;
}

export function ProviderButtons() {
  const providers = useQuery({
    queryKey: ['sign-in-providers'],
    queryFn: api.signInProviders,
    // The answer changes only when an admin edits Settings, and this runs on
    // the busiest page in the app.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const available = providers.data?.providers ?? [];
  if (available.length === 0) return null;

  const embedded = inAppBrowser();

  return (
    <div className="space-y-3">
      {/* A rule with a word in it, so the two ways in read as alternatives
          rather than as a form that continues below the button. */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      {embedded && (
        <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          You have opened this from <strong>{embedded}</strong>, which uses its own browser.
          Signing in with Google or GitHub will not work here — tap the menu and choose
          &ldquo;Open in Chrome&rdquo;, or sign in with your email and password below.
        </p>
      )}

      {available.map((provider) => (
        /*
         * A plain link, not a fetch.
         *
         * The server sets an HttpOnly state cookie and redirects to the
         * provider, and a full navigation is the only thing that carries both
         * reliably. An XHR would need the redirect followed by hand, would lose
         * the cookie in some browsers, and would break the back button.
         */
        <a
          key={provider.provider}
          href={provider.startPath}
          className="btn-secondary w-full justify-center"
        >
          <ProviderMark provider={provider.provider} />
          Continue with {provider.label}
        </a>
      ))}

      <p className="text-center text-xs text-muted-foreground">
        If you have not been given an account yet, this asks for one. Somebody here has to approve
        it before you can sign in.
      </p>
    </div>
  );
}

/**
 * The provider's mark, drawn rather than fetched.
 *
 * Inline SVG because the artifact's own rule applies here too: a remote logo is
 * a request to a third party from the sign-in page, which is both a privacy
 * leak and a thing that renders as a broken image on the Kisii connection this
 * console actually runs on. Monochrome, so neither one competes with the single
 * orange action above.
 */
function ProviderMark({ provider }: { provider: string }) {
  const common = {
    className: 'h-4 w-4 shrink-0',
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    'aria-hidden': true,
  } as const;

  if (provider === 'github') {
    return (
      <svg {...common}>
        <path d="M12 .5A11.5 11.5 0 0 0 .5 12a11.5 11.5 0 0 0 7.86 10.92c.58.1.79-.25.79-.55v-1.94c-3.2.7-3.87-1.54-3.87-1.54-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.04 1.78 2.72 1.27 3.38.97.1-.75.4-1.27.73-1.56-2.55-.29-5.24-1.28-5.24-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.51-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.24 2.76.12 3.05.74.81 1.19 1.84 1.19 3.1 0 4.43-2.7 5.4-5.26 5.69.41.36.78 1.06.78 2.14v3.17c0 .3.2.66.8.55A11.5 11.5 0 0 0 23.5 12A11.5 11.5 0 0 0 12 .5Z" />
      </svg>
    );
  }

  if (provider === 'microsoft') {
    return (
      <svg {...common} viewBox="0 0 24 24">
        <path d="M3 3h8.5v8.5H3V3Zm9.5 0H21v8.5h-8.5V3ZM3 12.5h8.5V21H3v-8.5Zm9.5 0H21V21h-8.5v-8.5Z" />
      </svg>
    );
  }

  // Google's G, in one colour. The four-colour mark is a trademark with usage
  // rules; a monochrome glyph beside the word "Google" is the safe version and
  // suits the theme better anyway.
  return (
    <svg {...common}>
      <path d="M12 11v2.8h6.9c-.3 1.7-2 5-6.9 5a7.1 7.1 0 0 1 0-14.2c2 0 3.4.9 4.2 1.6l2.1-2A9.6 9.6 0 0 0 12 1.6a10.4 10.4 0 0 0 0 20.8c6 0 9.9-4.2 9.9-10.1 0-.5 0-.9-.1-1.3H12Z" />
    </svg>
  );
}
